import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { 
  Network, 
  Building2, 
  MapPin, 
  Mail, 
  User, 
  Briefcase,
  Tractor,
  Factory,
  HeartHandshake,
  Landmark,
  GraduationCap,
  HelpCircle,
  ArrowRight,
  Check,
  Leaf,
  Globe,
  Shield
} from 'lucide-react';
import { motion } from 'framer-motion';

type UserType = 'farmer' | 'buyer' | 'ngo' | 'government' | 'researcher' | 'other';

interface FormData {
  name: string;
  email: string;
  organization: string;
  userType: UserType;
  location: string;
  interests: string[];
  farmSize?: string;
  mainCrops?: string[];
  challenges?: string[];
  marketNeeds?: string[];
  researchFocus?: string[];
  message?: string;
}

const SignupPage: React.FC = () => {
  const { register, handleSubmit, watch, formState: { errors } } = useForm<FormData>();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  
  const userType = watch('userType');
  
  const userTypeOptions = [
    { value: 'farmer', label: 'Farmer / Co-op', icon: <Tractor className="h-5 w-5" /> },
    { value: 'buyer', label: 'Buyer / Retailer', icon: <Factory className="h-5 w-5" /> },
    { value: 'ngo', label: 'NGO / Foundation', icon: <HeartHandshake className="h-5 w-5" /> },
    { value: 'government', label: 'Government', icon: <Landmark className="h-5 w-5" /> },
    { value: 'researcher', label: 'Researcher', icon: <GraduationCap className="h-5 w-5" /> },
    { value: 'other', label: 'Other', icon: <HelpCircle className="h-5 w-5" /> }
  ];
  
  const onSubmit = async (data: FormData) => {
    setSubmitting(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    console.log('Form submitted:', data);
    setSuccess(true);
    setSubmitting(false);
  };
  
  const nextStep = () => {
    setStep(step + 1);
    window.scrollTo(0, 0);
  };
  
  const prevStep = () => {
    setStep(step - 1);
    window.scrollTo(0, 0);
  };
  
  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 flex items-center">
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-md mx-auto bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="h-8 w-8 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-4 font-montserrat">Thank You!</h1>
            <p className="text-gray-600 mb-6">
              You've joined a growing global network rooted in justice. Check your inbox to verify and start your journey with AVN.
            </p>
            <Link 
              to="/"
              className="inline-flex items-center gap-2 text-[#4B0082] hover:text-[#4B0082]/80"
            >
              <span>Return to Home</span>
              <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-block p-4 bg-white rounded-full shadow-md mb-4">
              <Network className="h-12 w-12 text-[#4B0082]" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4 font-montserrat">Become part of a regenerative food system</h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto font-open-sans">
              Join our network of farmers, businesses, and partners working together to build a more just and sustainable food future.
            </p>
            <div className="flex flex-wrap justify-center gap-4 mt-6">
              <div className="flex items-center gap-2 bg-green-50 px-3 py-1 rounded-full text-green-700">
                <Leaf className="h-4 w-4" />
                <span className="text-sm">Sustainable Farming</span>
              </div>
              <div className="flex items-center gap-2 bg-blue-50 px-3 py-1 rounded-full text-blue-700">
                <Globe className="h-4 w-4" />
                <span className="text-sm">Global Network</span>
              </div>
              <div className="flex items-center gap-2 bg-purple-50 px-3 py-1 rounded-full text-purple-700">
                <Shield className="h-4 w-4" />
                <span className="text-sm">Ethical Verification</span>
              </div>
            </div>
          </div>

          {/* Progress Steps */}
          <div className="mb-8">
            <div className="flex justify-between items-center">
              {[1, 2, 3].map((s) => (
                <div
                  key={s}
                  className={`flex items-center ${
                    s < step
                      ? 'text-green-600'
                      : s === step
                      ? 'text-[#4B0082]'
                      : 'text-gray-300'
                  }`}
                >
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      s < step 
                        ? 'bg-green-100' 
                        : s === step 
                        ? 'bg-[#4B0082]/10' 
                        : 'bg-gray-100'
                    }`}
                  >
                    {s < step ? (
                      <Check className="h-5 w-5" />
                    ) : (
                      <span>{s}</span>
                    )}
                  </div>
                  {s < 3 && (
                    <div
                      className={`w-full h-1 ${
                        s < step ? 'bg-green-600' : 'bg-gray-200'
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>
            <div className="flex justify-between mt-2">
              <span className="text-sm text-gray-600">Basic Info</span>
              <span className="text-sm text-gray-600">Your Role</span>
              <span className="text-sm text-gray-600">Interests</span>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="bg-white rounded-xl shadow-lg p-8">
            {step === 1 && (
              <div className="space-y-6">
                <h2 className="text-xl font-semibold mb-4 font-montserrat">Basic Information</h2>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Full Name *
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      className={`pl-10 w-full px-4 py-2 rounded-lg border ${errors.name ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-[#4B0082] focus:border-[#4B0082]`}
                      placeholder="Your name"
                      {...register('name', { required: 'Name is required' })}
                    />
                  </div>
                  {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address *
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="email"
                      className={`pl-10 w-full px-4 py-2 rounded-lg border ${errors.email ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-[#4B0082] focus:border-[#4B0082]`}
                      placeholder="Your email"
                      {...register('email', { 
                        required: 'Email is required',
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: 'Invalid email address'
                        }
                      })}
                    />
                  </div>
                  {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Organization/Farm Name *
                  </label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      className={`pl-10 w-full px-4 py-2 rounded-lg border ${errors.organization ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-[#4B0082] focus:border-[#4B0082]`}
                      placeholder="Your organization or farm"
                      {...register('organization', { required: 'Organization is required' })}
                    />
                  </div>
                  {errors.organization && <p className="text-red-500 text-sm mt-1">{errors.organization.message}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Location *
                  </label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      className={`pl-10 w-full px-4 py-2 rounded-lg border ${errors.location ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-[#4B0082] focus:border-[#4B0082]`}
                      placeholder="City, Country"
                      {...register('location', { required: 'Location is required' })}
                    />
                  </div>
                  {errors.location && <p className="text-red-500 text-sm mt-1">{errors.location.message}</p>}
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-6">
                <h2 className="text-xl font-semibold mb-4 font-montserrat">Your Role</h2>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    I am a... *
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {userTypeOptions.map((type) => (
                      <label
                        key={type.value}
                        className={`flex flex-col items-center gap-2 p-3 rounded-lg border cursor-pointer transition-all ${
                          userType === type.value
                            ? 'bg-[#4B0082]/10 border-[#4B0082]/30 shadow-md'
                            : 'bg-white border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="radio"
                          value={type.value}
                          className="sr-only"
                          {...register('userType', { required: 'Please select your role' })}
                        />
                        <div className={`p-2 rounded-full ${
                          userType === type.value ? 'bg-[#4B0082]/20' : 'bg-gray-100'
                        }`}>
                          {type.icon}
                        </div>
                        <span className="text-sm text-center">{type.label}</span>
                      </label>
                    ))}
                  </div>
                  {errors.userType && <p className="text-red-500 text-sm mt-1">{errors.userType.message}</p>}
                </div>

                {userType === 'farmer' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Farm Size (hectares/acres)
                    </label>
                    <input
                      type="text"
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#4B0082] focus:border-[#4B0082]"
                      placeholder="e.g., 5 hectares"
                      {...register('farmSize')}
                    />
                  </div>
                )}

                {userType === 'buyer' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Business Type
                    </label>
                    <select
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#4B0082] focus:border-[#4B0082]"
                      {...register('marketNeeds')}
                    >
                      <option value="">Select business type</option>
                      <option value="retailer">Retailer</option>
                      <option value="wholesaler">Wholesaler</option>
                      <option value="restaurant">Restaurant/Food Service</option>
                      <option value="processor">Processor/Manufacturer</option>
                      <option value="exporter">Exporter/Importer</option>
                    </select>
                  </div>
                )}

                {userType === 'researcher' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Research Focus
                    </label>
                    <select
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#4B0082] focus:border-[#4B0082]"
                      {...register('researchFocus')}
                    >
                      <option value="">Select research focus</option>
                      <option value="agronomy">Agronomy</option>
                      <option value="climate">Climate Resilience</option>
                      <option value="economics">Agricultural Economics</option>
                      <option value="technology">AgTech/Innovation</option>
                      <option value="policy">Policy Research</option>
                    </select>
                  </div>
                )}
              </div>
            )}

            {step === 3 && (
              <div className="space-y-6">
                <h2 className="text-xl font-semibold mb-4 font-montserrat">Areas of Interest</h2>
                
                {userType === 'farmer' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Main Crops
                      </label>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {['Coffee', 'Cocoa', 'Maize', 'Rice', 'Vegetables', 'Fruits', 'Other'].map(crop => (
                          <label
                            key={crop}
                            className="flex items-center gap-2 p-2 rounded border cursor-pointer hover:bg-gray-50"
                          >
                            <input
                              type="checkbox"
                              value={crop}
                              className="rounded text-[#4B0082]"
                              {...register('mainCrops')}
                            />
                            <span className="text-sm">{crop}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Current Challenges
                      </label>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {['Market Access', 'Water Management', 'Pest Control', 'Climate Change', 'Soil Health', 'Technology Access'].map(challenge => (
                          <label
                            key={challenge}
                            className="flex items-center gap-2 p-2 rounded border cursor-pointer hover:bg-gray-50"
                          >
                            <input
                              type="checkbox"
                              value={challenge}
                              className="rounded text-[#4B0082]"
                              {...register('challenges')}
                            />
                            <span className="text-sm">{challenge}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </>
                )}
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    What interests you about AVN? *
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {[
                      'Ethical Verification', 
                      'Regenerative Farming', 
                      'Market Access', 
                      'Technology', 
                      'Climate Resilience',
                      'Fair Compensation',
                      'Community Building',
                      'Supply Chain Transparency'
                    ].map(interest => (
                      <label
                        key={interest}
                        className="flex items-center gap-2 p-2 rounded border cursor-pointer hover:bg-gray-50"
                      >
                        <input
                          type="checkbox"
                          value={interest}
                          className="rounded text-[#4B0082]"
                          {...register('interests', { required: 'Please select at least one interest' })}
                        />
                        <span className="text-sm">{interest}</span>
                      </label>
                    ))}
                  </div>
                  {errors.interests && <p className="text-red-500 text-sm mt-1">{errors.interests.message}</p>}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Additional Information (Optional)
                  </label>
                  <textarea
                    rows={4}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#4B0082] focus:border-[#4B0082]"
                    placeholder="Tell us more about your interests or how you'd like to engage with AVN..."
                    {...register('message')}
                  ></textarea>
                </div>
              </div>
            )}

            <div className="mt-8 flex justify-between">
              {step > 1 ? (
                <button
                  type="button"
                  onClick={prevStep}
                  className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Back
                </button>
              ) : (
                <div></div>
              )}

              {step < 3 ? (
                <button
                  type="button"
                  onClick={nextStep}
                  className="px-6 py-2 bg-[#4B0082] text-white rounded-lg hover:bg-[#3b0065] transition-colors"
                >
                  Next
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={submitting}
                  className={`px-8 py-3 bg-[#4B0082] text-white rounded-lg hover:bg-[#3b0065] transition-colors flex items-center gap-2 ${
                    submitting ? 'opacity-75 cursor-not-allowed' : ''
                  }`}
                >
                  {submitting ? (
                    <>
                      <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      <span>Complete Registration</span>
                      <ArrowRight className="h-5 w-5" />
                    </>
                  )}
                </button>
              )}
            </div>
          </form>

          <div className="text-center mt-8">
            <p className="text-gray-600">
              Already have an account?{' '}
              <Link to="/login" className="text-[#4B0082] hover:text-[#4B0082]/80 font-medium">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;